import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { API_BASE, APP_STORE_URL, buildAppStoreUrl } from "@/lib/constants";
import { trackEvent } from "@/lib/analytics";

/**
 * "Stay in the loop" email opt-in page. Designed to be linked from social
 * bios (IG, FB, X, TikTok) where the visitor may not be ready to download
 * yet but wants product updates.
 *
 * URL patterns:
 *   /subscribe               → source = "subscribe_page"
 *   /subscribe?src=ig        → source = "ig_subscribe"
 *   /subscribe?src=fb        → source = "fb_subscribe"
 *   /subscribe?src=tt        → source = "tt_subscribe"
 *   /subscribe?src=<any>     → source = "<any>_subscribe"
 *
 * On submit → POSTs to /api/waitlist with the resolved source so the admin
 * dashboard shows a clean breakdown of which bio link converted.
 */

const ROLE_OPTIONS = [
  "ER RN",
  "ICU RN",
  "Charge / Rapid Response",
  "Flight / Transport",
  "Nursing Student",
  "Other clinician",
];

export default function Subscribe() {
  const [searchParams] = useSearchParams();
  const src = (searchParams.get("src") || "").trim().toLowerCase();
  const source = src ? `${src}_subscribe` : "subscribe_page";

  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Stay in the loop · Stable-ish";
    trackEvent("page_view", "subscribe-page", { src: source });
  }, [source]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Enter your email first.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/waitlist`, {
        email: email.trim(),
        role: role || null,
        source,
      });
      setDone(true);
      trackEvent("download_click", "subscribe-complete", {
        source,
        role: role || "unspecified",
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't add you. Try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      className="min-h-screen bg-[#050505] text-white noise"
      data-testid="subscribe-page"
    >
      <div className="max-w-[720px] mx-auto px-5 md:px-10 pt-14 md:pt-24 pb-24">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-white/45 hover:text-[#FF2A2A] transition-colors mb-10"
          data-testid="subscribe-back-link"
        >
          <ArrowLeft size={14} weight="bold" />
          back to home
        </Link>

        {!done ? (
          <>
            <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A]">
              // opt in · unsubscribe any time
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[0.95]">
              Stay in <span className="italic font-light text-white/70">the loop.</span>
            </h1>
            <p className="mt-5 text-base text-white/65 max-w-lg leading-relaxed">
              Get notified about new bedside tools, product updates, and when
              Stable-ish ships on Android.
            </p>

            <form
              onSubmit={onSubmit}
              className="mt-10 space-y-4"
              data-testid="subscribe-form"
            >
              <div>
                <label
                  htmlFor="sub-email"
                  className="block font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2"
                >
                  email
                </label>
                <input
                  id="sub-email"
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@hospital.org"
                  data-testid="subscribe-email-input"
                  className="w-full bg-black border border-white/20 focus:border-[#FF2A2A] hover:border-white/40 text-white/95 px-4 py-4 text-base focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label
                  htmlFor="sub-role"
                  className="block font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2"
                >
                  role <span className="text-white/30">(optional)</span>
                </label>
                <select
                  id="sub-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  data-testid="subscribe-role-select"
                  className="w-full bg-black border border-white/20 focus:border-[#FF2A2A] hover:border-white/40 text-white/95 px-4 py-4 text-base focus:outline-none transition-colors"
                >
                  <option value="">— pick one —</option>
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="subscribe-submit"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-50 disabled:cursor-not-allowed text-white font-display font-bold text-lg tracking-tight px-8 py-4 transition-colors"
              >
                {submitting ? "Adding…" : "Sign me up →"}
              </button>
            </form>

            <div className="mt-10 pt-8 border-t border-white/10">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/45 mb-3">
                // already convinced?
              </div>
              <a
                href={buildAppStoreUrl(`${src || "subscribe"}_direct`)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackEvent("download_click", "subscribe-page-direct", {
                    source,
                    store: "apple",
                  })
                }
                data-testid="subscribe-direct-download"
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-white/70 hover:text-[#FF2A2A] underline decoration-white/20 underline-offset-4 transition-colors"
              >
                Skip the loop — download Stable-ish now
              </a>
            </div>
          </>
        ) : (
          <div data-testid="subscribe-success">
            <div className="mb-4 flex items-center gap-3 text-[#FF2A2A]">
              <CheckCircle size={28} weight="fill" />
              <div className="font-mono text-[10px] uppercase tracking-[0.28em]">
                // you&rsquo;re in
              </div>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[0.95]">
              Welcome to<br />
              <span className="italic font-light text-white/70">the loop.</span>
            </h1>
            <p className="mt-5 text-base text-white/65 max-w-lg leading-relaxed">
              A confirmation is on its way. Feature requests are always
              welcome — every reply gets read.
            </p>

            {/* Immediate secondary CTAs — this is peak enthusiasm */}
            <div className="mt-10 space-y-4">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackEvent("download_click", "subscribe-thanks", {
                    source,
                    store: "apple",
                  })
                }
                data-testid="subscribe-thanks-download"
                className="block w-full text-center bg-[#FF2A2A] hover:bg-[#FF4D4D] text-white font-display font-bold text-lg tracking-tight px-6 py-4 transition-colors"
              >
                Download Stable-ish · 14 days free →
              </a>
              <Link
                to="/share"
                onClick={() =>
                  trackEvent("share_click", "subscribe-thanks", {
                    channel: "share_page_referral",
                  })
                }
                data-testid="subscribe-thanks-share"
                className="block w-full text-center border border-white/20 hover:border-white/50 text-white/85 font-mono text-[11px] uppercase tracking-[0.22em] px-6 py-4 transition-colors"
              >
                Or share it with a nurse friend
              </Link>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
